import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Send, MessageCircle, Phone, Loader2 } from "lucide-react";
import { Reveal } from "../motion";
import { DESTINATIONS, SERVICES, CONTACT } from "../../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = "text", value, onChange, required, placeholder }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-sm font-medium text-slate-600">{label}{required && <span className="text-teal"> *</span>}</span>
    <input type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder}
      data-testid={`lead-${name}`}
      className="rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:ring-2 focus:ring-teal focus:border-teal transition-shadow" />
  </label>
);

const empty = { full_name: "", phone: "", email: "", current_country: "", preferred_destination: "",
  service_required: "", education: "", work_experience: "", message: "" };

export const LeadForm = () => {
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please provide consent to continue.");
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("consent", "true");
      if (file) fd.append("resume", file);
      await axios.post(`${API}/leads`, fd);
      toast.success("Thank you! Our team will contact you shortly.");
      setForm(empty); setFile(null); setConsent(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <section id="consultation" className="relative bg-midnight py-24 md:py-32 grain overflow-hidden" data-testid="consultation-section">
      <div className="absolute inset-0" style={{ background: "radial-gradient(90% 60% at 80% 10%, rgba(212,175,55,0.12), transparent 60%)" }} />
      <div className="relative z-10 max-w-6xl mx-auto px-5 lg:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-4">Start Your Global Journey Today</p>
          <h2 className="font-serifd text-4xl md:text-5xl lg:text-6xl text-white font-light max-w-2xl leading-tight">
            Request your free consultation
          </h2>
          <p className="mt-4 text-slate-300 max-w-xl">Share your details and our consultants will assess your profile and recommend suitable pathways.</p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-3 gap-8">
          <Reveal className="lg:col-span-2">
            <form onSubmit={submit} className="rounded-2xl bg-white p-6 md:p-8 grid md:grid-cols-2 gap-5" data-testid="lead-form">
              <Field label="Full Name" name="full_name" value={form.full_name} onChange={set} required />
              <Field label="Phone Number" name="phone" value={form.phone} onChange={set} required />
              <Field label="Email Address" name="email" type="email" value={form.email} onChange={set} required />
              <Field label="Current Country" name="current_country" value={form.current_country} onChange={set} />

              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-slate-600">Preferred Destination</span>
                <select name="preferred_destination" value={form.preferred_destination} onChange={set} data-testid="lead-preferred_destination"
                  className="rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:ring-2 focus:ring-teal">
                  <option value="">Select</option>
                  {DESTINATIONS.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
                  <option value="Other">Other</option>
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-slate-600">Service Required</span>
                <select name="service_required" value={form.service_required} onChange={set} data-testid="lead-service_required"
                  className="rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:ring-2 focus:ring-teal">
                  <option value="">Select</option>
                  {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                </select>
              </label>

              <Field label="Education" name="education" value={form.education} onChange={set} placeholder="Highest qualification" />
              <Field label="Work Experience" name="work_experience" value={form.work_experience} onChange={set} placeholder="Years / field" />

              <label className="flex flex-col gap-1.5 md:col-span-2">
                <span className="text-sm font-medium text-slate-600">Message</span>
                <textarea name="message" value={form.message} onChange={set} rows={3} data-testid="lead-message"
                  className="rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:ring-2 focus:ring-teal resize-none" />
              </label>

              <label className="md:col-span-2 flex items-center gap-3 rounded-lg border border-dashed border-slate-300 px-4 py-3 cursor-pointer hover:border-teal transition-colors" data-testid="lead-upload-label">
                <Upload size={18} className="text-teal" />
                <span className="text-sm text-slate-600">{file ? file.name : "Upload Resume / Document (PDF, DOCX — max 8MB)"}</span>
                <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden"
                  onChange={(e) => setFile(e.target.files[0])} data-testid="lead-resume" />
              </label>

              <label className="md:col-span-2 flex items-start gap-3 text-sm text-slate-600">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-teal h-4 w-4" data-testid="lead-consent" />
                <span>I consent to Global Connect contacting me regarding my enquiry. I understand immigration and visa decisions are made by the relevant government authorities.</span>
              </label>

              <button type="submit" disabled={loading} data-testid="lead-submit-btn"
                className="md:col-span-2 rounded-full bg-primary-blue text-white py-4 font-semibold flex items-center justify-center gap-2 hover:bg-teal transition-colors disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Request Consultation
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="flex flex-col gap-4">
            <a href={`https://wa.me/${CONTACT.whatsappLink}`} target="_blank" rel="noreferrer" data-testid="consult-whatsapp-btn"
              className="rounded-2xl bg-[#25D366] text-white p-6 flex items-center gap-4 hover:brightness-110 transition-all">
              <MessageCircle size={28} /><div><p className="font-semibold">WhatsApp Us</p><p className="text-sm text-white/85">{CONTACT.whatsapp}</p></div>
            </a>
            <a href={`tel:${CONTACT.phoneLink}`} data-testid="consult-call-btn"
              className="rounded-2xl bg-white/5 border border-white/15 text-white p-6 flex items-center gap-4 hover:bg-white/10 transition-colors">
              <Phone size={28} className="text-gold" /><div><p className="font-semibold">Call Us</p><p className="text-sm text-slate-300">{CONTACT.phone}</p></div>
            </a>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-slate-300 text-sm leading-relaxed">
              Global Connect provides consultancy and application assistance. We do not guarantee visas, jobs, or immigration outcomes — decisions rest with the relevant authorities.
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
